import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { humanizeSupabaseError } from "@/lib/supabase-errors";
import type { TaskPriority } from "@/lib/task-priority";

export type { TaskPriority };
export type TaskFilter = "today" | "overdue" | "upcoming" | "completed" | "all";
export type TaskSort = "due_date" | "updated_at" | "title" | "priority";

export interface WorkspaceTask {
  id: string;
  workspace_id: string;
  title: string;
  description: string | null;
  due_date: string | null;
  due_at: string | null;
  status: "pending" | "completed" | "cancelled";
  outcome: "positive" | "negative" | null;
  completed_at: string | null;
  assigned_member_id: string;
  created_by_member_id: string | null;
  updated_at: string;
  priority?: TaskPriority | null;
  lead_id?: string | null;
  tags?: string[] | null;
  parent_task_id?: string | null;
  order_index?: number | null;
  project_id?: string | null;
  project_column_id?: string | null;
  board_order_index?: number | null;
  assignee?: { display_name: string | null } | null;
  creator?: { display_name: string | null } | null;
  lead?: { id: string; name: string } | null;
  /** Subtarefas (apenas id/status) usadas para badge de progresso. */
  subtasks?: { id: string; status: WorkspaceTask["status"] }[] | null;
}

export interface SubtaskProgress {
  total: number;
  done: number;
}

/** Conta subtarefas não canceladas e quantas estão concluídas. */
export function getSubtaskProgress(task: WorkspaceTask): SubtaskProgress {
  const subs = (task.subtasks ?? []).filter((s) => s.status !== "cancelled");
  return {
    total: subs.length,
    done: subs.filter((s) => s.status === "completed").length,
  };
}

export type CancelTaskResult = "cancelled" | "requested" | "already_requested";

export interface CreateSubtaskInput {
  title: string;
  description?: string | null;
  assignee_member_ids: string[];
  due_date?: string | null;
  due_at?: string | null;
}

export interface CreateTaskInput {
  title: string;
  description?: string | null;
  assigned_member_id: string;
  due_date: string | null;
  due_at?: string | null;
  priority?: TaskPriority;
  lead_id?: string | null;
  tags?: string[];
  /** Subtarefas em rascunho criadas junto da tarefa-raiz. */
  subtasks?: CreateSubtaskInput[];
  project_id?: string | null;
  project_column_id?: string | null;
}

export interface UpdateTaskInput {
  taskId: string;
  title: string;
  description?: string | null;
  assigned_member_id: string;
  due_date: string | null;
  due_at?: string | null;
  priority?: TaskPriority;
  lead_id?: string | null;
  tags?: string[];
  allowPastDue?: boolean;
  project_id?: string | null;
  project_column_id?: string | null;
}

const TASK_LIST_LIMIT = 50;

const SUBTASK_EMBED = `subtasks:workspace_tasks!parent_task_id(id, status)`;

const BASE_SELECT = `id, workspace_id, title, description, due_date, due_at, status, outcome,
  completed_at, assigned_member_id, created_by_member_id, updated_at,
  assignee:assigned_member_id(display_name),
  creator:created_by_member_id(display_name),
  ${SUBTASK_EMBED}`;

const SELECT_WITH_EXTRAS = `${BASE_SELECT}, priority, lead_id, tags, lead:lead_id(id, name),
  project_id, project_column_id, board_order_index`;

const PRIORITY_ORDER: Record<TaskPriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export function taskPriorityValue(task: WorkspaceTask): TaskPriority {
  return task.priority ?? "medium";
}

export function todayInWorkspaceTz(timezone = "America/Sao_Paulo"): string {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(now); // YYYY-MM-DD
}

function addDaysISO(dateIso: string, days: number): string {
  const d = new Date(dateIso + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Início do dia há N dias (para filtro de concluídas). */
function daysAgoIso(today: string, days: number): string {
  return addDaysISO(today, -days);
}

function escapeIlike(term: string): string {
  return term.replace(/[%_\\]/g, "\\$&");
}

/** Pendentes primeiro; entre pendentes, prazo mais próximo no topo; concluídas por data de conclusão. */
function sortAllFilterTasks(tasks: WorkspaceTask[]): WorkspaceTask[] {
  const statusOrder: Record<WorkspaceTask["status"], number> = {
    pending: 0,
    completed: 1,
    cancelled: 2,
  };

  return [...tasks].sort((a, b) => {
    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    if (statusDiff !== 0) return statusDiff;

    if (a.status === "completed") {
      const aTime = a.completed_at ? new Date(a.completed_at).getTime() : 0;
      const bTime = b.completed_at ? new Date(b.completed_at).getTime() : 0;
      return bTime - aTime;
    }

    if (!a.due_date && !b.due_date) {
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    }
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;

    const dateDiff = a.due_date.localeCompare(b.due_date);
    if (dateDiff !== 0) return dateDiff;

    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });
}

function sortTasksByPriority(tasks: WorkspaceTask[]): WorkspaceTask[] {
  return [...tasks].sort((a, b) => {
    const diff = PRIORITY_ORDER[taskPriorityValue(a)] - PRIORITY_ORDER[taskPriorityValue(b)];
    if (diff !== 0) return diff;
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });
}

function applyPostFetchSort(
  rows: WorkspaceTask[],
  filter: TaskFilter,
  sort: TaskSort,
): WorkspaceTask[] {
  if (sort === "priority") return sortTasksByPriority(rows);
  if (filter === "all" && sort === "due_date") return sortAllFilterTasks(rows);
  return rows;
}

interface UseWorkspaceTasksArgs {
  workspaceId: string | null;
  memberId: string | null;
  isAdmin: boolean;
  filter: TaskFilter;
  /** Quando true, força exibir apenas as próprias tarefas mesmo para gestor. */
  onlyMine?: boolean;
  timezone?: string;
  searchQuery?: string;
  /** Filtro por responsável na aba Equipe (somente admin). */
  assigneeId?: string | null;
  sort?: TaskSort;
  /** Filtra tarefas de um projeto específico. */
  projectId?: string | null;
  /** Quando true, retorna apenas tarefas órfãs (project_id IS NULL). */
  orphanOnly?: boolean;
  /** Quando não vazio, restringe às prioridades selecionadas. */
  priorities?: TaskPriority[];
}

export function useWorkspaceTasks({
  workspaceId,
  memberId,
  isAdmin,
  filter,
  onlyMine = false,
  timezone,
  searchQuery = "",
  assigneeId = null,
  sort = "due_date",
  projectId = null,
  orphanOnly = false,
  priorities = [],
}: UseWorkspaceTasksArgs) {
  const [tasks, setTasks] = useState<WorkspaceTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const { toast } = useToast();
  const priorityColumnAvailable = useRef<boolean | null>(null);
  const tasksLengthRef = useRef(0);
  tasksLengthRef.current = tasks.length;

  const fetchTasks = useCallback(
    async (options: { loadMore?: boolean } = {}) => {
      if (!workspaceId) return;

      const loadMore = options.loadMore === true;
      const offset = loadMore ? tasksLengthRef.current : 0;

      if (loadMore) setLoadingMore(true);
      else setLoading(true);

      try {
        const today = todayInWorkspaceTz(timezone);
        const trimmedSearch = searchQuery.trim();

        const runQuery = (select: string) => {
          let query = supabase
            .from("workspace_tasks")
            .select(select)
            .eq("workspace_id", workspaceId)
            .is("parent_task_id", null);

          if (trimmedSearch) {
            query = query.ilike("title", `%${escapeIlike(trimmedSearch)}%`);
          }

          if (orphanOnly) {
            query = query.is("project_id", null);
          } else if (projectId) {
            query = query.eq("project_id", projectId);
          }

          if ((!isAdmin || onlyMine) && memberId) {
            query = query.eq("assigned_member_id", memberId);
          } else if (assigneeId) {
            query = query.eq("assigned_member_id", assigneeId);
          }

          if (priorities.length > 0) {
            query = query.in("priority", priorities);
          }

          // Gerais (órfãs): mesma base que useOrphanTasks — sem filtros temporais.
          if (orphanOnly) {
            query = query.neq("status", "cancelled");
          } else {
            switch (filter) {
              case "today":
                query = query.eq("due_date", today).eq("status", "pending");
                break;
              case "overdue":
                query = query.lt("due_date", today).eq("status", "pending");
                break;
              case "upcoming":
                query = query.gt("due_date", today).eq("status", "pending");
                break;
              case "completed":
                query = query
                  .eq("status", "completed")
                  .gte("completed_at", `${daysAgoIso(today, 30)}T00:00:00.000Z`);
                break;
              case "all":
              default:
                query = query.neq("status", "cancelled");
                break;
            }
          }

          if (sort === "updated_at") {
            query = query.order("updated_at", { ascending: false });
          } else if (sort === "title") {
            query = query.order("title", { ascending: true });
          } else if (sort === "priority" && priorityColumnAvailable.current !== false) {
            query = query.order("priority", { ascending: false, nullsFirst: false });
            query = query.order("updated_at", { ascending: false });
          } else {
            const orderAscending = filter === "completed";
            query = query.order(orderAscending ? "completed_at" : "due_date", {
              ascending: !orderAscending,
              nullsFirst: false,
            });
            query = query.order("updated_at", { ascending: false });
          }

          return query.range(offset, offset + TASK_LIST_LIMIT - 1);
        };

        const useExtrasSelect = priorityColumnAvailable.current !== false;
        let select = useExtrasSelect ? SELECT_WITH_EXTRAS : BASE_SELECT;
        let result = await runQuery(select);

        if (
          result.error &&
          /priority|lead_id|tags|project_id|project_column_id|board_order_index/i.test(
            result.error.message ?? "",
          )
        ) {
          priorityColumnAvailable.current = false;
          select = BASE_SELECT;
          result = await runQuery(select);
        } else if (!result.error && useExtrasSelect) {
          priorityColumnAvailable.current = true;
        }

        let { data, error } = result;

        if (error && sort === "priority" && /priority/i.test(error.message ?? "")) {
          priorityColumnAvailable.current = false;
          ({ data, error } = await runQuery(BASE_SELECT));
        }

        if (error) throw error;

        const rows = applyPostFetchSort(
          (data ?? []) as unknown as WorkspaceTask[],
          filter,
          sort,
        );

        setHasMore(rows.length === TASK_LIST_LIMIT);
        setTasks((prev) => (loadMore ? applyPostFetchSort([...prev, ...rows], filter, sort) : rows));
      } catch (err: unknown) {
        console.error("useWorkspaceTasks.fetchTasks:", err);
        toast({
          title: "Erro ao carregar tarefas",
          description: humanizeSupabaseError(err, {
            permissionMessage: "Você não tem permissão para ver estas tarefas.",
            fallback: "Não foi possível carregar as tarefas. Tente novamente.",
          }),
          variant: "destructive",
        });
      } finally {
        if (loadMore) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [
      workspaceId,
      memberId,
      isAdmin,
      filter,
      onlyMine,
      timezone,
      searchQuery,
      assigneeId,
      sort,
      projectId,
      orphanOnly,
      priorities,
      toast,
    ],
  );

  const loadMore = useCallback(async () => {
    await fetchTasks({ loadMore: true });
  }, [fetchTasks]);

  useEffect(() => {
    void fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset list when filters change
  }, [
    workspaceId,
    memberId,
    isAdmin,
    filter,
    onlyMine,
    timezone,
    searchQuery,
    assigneeId,
    sort,
    projectId,
    orphanOnly,
    priorities,
  ]);

  const createTask = useCallback(
    async (input: CreateTaskInput) => {
      if (!workspaceId || !memberId) throw new Error("workspace ou membro ausente");

      if (input.due_date) {
        const today = todayInWorkspaceTz(timezone);
        if (input.due_date < today) {
          throw new Error("Não é possível criar tarefa com data no passado.");
        }
      }

      const tags = (input.tags ?? []).map((t) => t.trim()).filter(Boolean);

      const { data: created, error } = await supabase
        .from("workspace_tasks")
        .insert({
          workspace_id: workspaceId,
          title: input.title.trim(),
          description: input.description ?? null,
          assigned_member_id: input.assigned_member_id,
          created_by_member_id: memberId,
          due_date: input.due_date,
          due_at: input.due_date ? (input.due_at ?? null) : null,
          priority: input.priority ?? "medium",
          lead_id: input.lead_id ?? null,
          tags,
          status: "pending",
          project_id: input.project_id ?? null,
          project_column_id: input.project_column_id ?? null,
        })
        .select("id")
        .single();
      if (error) throw error;

      const drafts = (input.subtasks ?? []).filter((s) => s.title.trim());
      if (drafts.length > 0 && created?.id) {
        const subtaskRows = drafts.map((s, index) => {
          const assigneeIds =
            s.assignee_member_ids.length > 0 ? s.assignee_member_ids : [memberId];
          return {
            workspace_id: workspaceId,
            parent_task_id: created.id,
            title: s.title.trim(),
            description: s.description?.trim() || null,
            assigned_member_id: assigneeIds[0],
            created_by_member_id: memberId,
            due_date: s.due_date ?? null,
            due_at: s.due_date ? (s.due_at ?? null) : null,
            order_index: index,
            status: "pending" as const,
          };
        });
        const { data: insertedSubtasks, error: subtaskError } = await supabase
          .from("workspace_tasks")
          .insert(subtaskRows)
          .select("id");
        if (subtaskError) throw subtaskError;

        const extraAssigneeRows = drafts.flatMap((s, index) => {
          const assigneeIds =
            s.assignee_member_ids.length > 0 ? s.assignee_member_ids : [memberId];
          const taskId = insertedSubtasks?.[index]?.id;
          if (!taskId || assigneeIds.length <= 1) return [];
          return assigneeIds.slice(1).map((member_id) => ({
            task_id: taskId,
            member_id,
            workspace_id: workspaceId,
          }));
        });
        if (extraAssigneeRows.length > 0) {
          const { error: assigneeError } = await supabase
            .from("workspace_task_assignees")
            .insert(extraAssigneeRows);
          if (assigneeError) throw assigneeError;
        }
      }

      await fetchTasks();
    },
    [workspaceId, memberId, timezone, fetchTasks],
  );

  const completeTask = useCallback(
    async (taskId: string, outcome: "positive" | "negative") => {
      const { error } = await supabase
        .from("workspace_tasks")
        .update({
          status: "completed",
          outcome,
          completed_at: new Date().toISOString(),
        })
        .eq("id", taskId);
      if (error) throw error;
      await fetchTasks();
    },
    [fetchTasks],
  );

  const reopenTask = useCallback(
    async (taskId: string) => {
      const { error } = await supabase
        .from("workspace_tasks")
        .update({ status: "pending", outcome: null, completed_at: null })
        .eq("id", taskId);
      if (error) throw error;
      await fetchTasks();
    },
    [fetchTasks],
  );

  const cancelTask = useCallback(
    async (taskId: string, reason?: string): Promise<CancelTaskResult> => {
      const { data, error } = await supabase.rpc("cancel_or_request_task_deletion", {
        p_task_id: taskId,
        p_reason: reason ?? undefined,
      });
      if (error) throw error;
      await fetchTasks();
      return (data as CancelTaskResult) ?? "cancelled";
    },
    [fetchTasks],
  );

  const updateTaskDueDate = useCallback(
    async (input: {
      taskId: string;
      due_date: string | null;
      due_at?: string | null;
      allowPastDue?: boolean;
    }) => {
      if (input.due_date && !input.allowPastDue) {
        const today = todayInWorkspaceTz(timezone);
        if (input.due_date < today) {
          throw new Error("Não é possível definir data no passado.");
        }
      }

      const { error } = await supabase
        .from("workspace_tasks")
        .update({
          due_date: input.due_date,
          due_at: input.due_date ? (input.due_at ?? null) : null,
        })
        .eq("id", input.taskId);
      if (error) throw error;
      await fetchTasks();
    },
    [timezone, fetchTasks],
  );

  const updateTask = useCallback(
    async (input: UpdateTaskInput) => {
      if (input.due_date && !input.allowPastDue) {
        const today = todayInWorkspaceTz(timezone);
        if (input.due_date < today) {
          throw new Error("Não é possível definir data no passado.");
        }
      }

      const tags = (input.tags ?? []).map((t) => t.trim()).filter(Boolean);

      const updatePayload: Record<string, unknown> = {
        title: input.title.trim(),
        description: input.description ?? null,
        assigned_member_id: input.assigned_member_id,
        due_date: input.due_date,
        due_at: input.due_date ? (input.due_at ?? null) : null,
        priority: input.priority ?? "medium",
        lead_id: input.lead_id ?? null,
        tags,
      };

      if (input.project_id !== undefined) {
        updatePayload.project_id = input.project_id;
        if (input.project_id) {
          updatePayload.project_column_id = input.project_column_id ?? null;
          if (input.project_column_id) {
            updatePayload.board_order_index = 0;
          }
        } else {
          updatePayload.project_column_id = null;
          updatePayload.board_order_index = 0;
        }
      } else if (input.project_column_id !== undefined) {
        updatePayload.project_column_id = input.project_column_id;
        if (input.project_column_id) {
          updatePayload.board_order_index = 0;
        }
      }

      const { error } = await supabase
        .from("workspace_tasks")
        .update(updatePayload)
        .eq("id", input.taskId);
      if (error) throw error;
      await fetchTasks();
    },
    [timezone, fetchTasks],
  );

  const duplicateTask = useCallback(
    async (source: WorkspaceTask) => {
      if (!workspaceId || !memberId) throw new Error("workspace ou membro ausente");

      const today = todayInWorkspaceTz(timezone);
      let due_date = source.due_date;
      if (due_date && due_date < today) {
        due_date = today;
      }

      const copyTitle = source.title.endsWith(" (cópia)")
        ? source.title
        : `${source.title} (cópia)`;

      await createTask({
        title: copyTitle,
        description: source.description,
        assigned_member_id: source.assigned_member_id,
        due_date,
        due_at: due_date && source.due_at ? source.due_at : null,
        priority: taskPriorityValue(source),
        lead_id: source.lead_id ?? null,
        tags: source.tags ?? [],
      });
    },
    [workspaceId, memberId, timezone, createTask],
  );

  const refetch = useCallback(async () => {
    await fetchTasks();
  }, [fetchTasks]);

  const moveTaskToColumn = useCallback(
    async (taskId: string, columnId: string, orderIndex?: number) => {
      const { error } = await supabase.rpc("move_task_to_column", {
        p_task_id: taskId,
        p_column_id: columnId,
        p_order_index: orderIndex ?? null,
      });
      if (error) throw error;
      await fetchTasks();
    },
    [fetchTasks],
  );

  return {
    tasks,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refetch,
    createTask,
    duplicateTask,
    completeTask,
    reopenTask,
    cancelTask,
    updateTaskDueDate,
    updateTask,
    moveTaskToColumn,
  };
}
